/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @version 1.24.0
 * @since 1.14.0
 */

import { Fragment, useEffect, useState } from 'react';

import { Formik, FormikHelpers } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { useInfiniteQuery } from '@/main/api/use-infinite-query';
import { icons } from '@assets/index';
import { Button, LinkBlock } from '@components/common';
import { ImgStatusError } from '@components/common/Block/Img';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import FormFieldInputSearch from '@modules/form/components/FormFieldInputSearch';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import Modal from '@modules/modal/components/Modal';
import ModalOrder from '@modules/modal/components/ModalOrder';
import { ModalCenterStyled } from '@modules/modal/components/styled/ModalCenterStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { manageNumberTranslationSelector } from '../selectors';
import {
  manageNumberApi,
  useGetManageNumberSimCardQuery,
  useUpdateManageNumberSimCardMutation,
} from '../store';
import type {
  IManageNumberQuery,
  IManageNumberSimCardQuery,
  IManageNumberSimCardUpdateResponse,
} from '../types';
import { getSimCardValidation } from '../utils';
import { ManageNumberSimCardNoDataStyled } from './styled/ManageNumberStyled';

interface IManageNumberSimCardModal
  extends Pick<IModalRenderProps, 'onCloseModal'> {
  isAllowSimCardSuggest: boolean;
  subscriberId: string;
}

const initialValue: { icc: IInputSearchItem } = {
  icc: { id: '', value: '', match: '' },
};

const ManageNumberSimCardModal = ({
  isAllowSimCardSuggest,
  subscriberId,
  onCloseModal,
}: IManageNumberSimCardModal) => {
  const dispatch = useDispatch();

  /* Local State */

  const [queryParams, setQueryParams] =
    useState<IManageNumberSimCardQuery | undefined>();

  /* Select State */

  const { form: tForm, modal: tModal } = useSelector(
    makeSelectTranslationCommon(),
  );
  const translationManageNumber = useSelector(
    manageNumberTranslationSelector(),
  );

  /* Api */

  const { data, isLoading, fetchNextPage, hasMore } = useInfiniteQuery<
    IInputSearchItem,
    IManageNumberQuery
  >(manageNumberApi.endpoints.getManageNumberSimCardSuggest);

  const { data: simCard, isLoading: isSimCardLoading, isError: isSimCardError } =
      useGetManageNumberSimCardQuery(subscriberId);

  const [updateSimCard, { data: simCardUpdateResult }] =
    useUpdateManageNumberSimCardMutation();

  /* Lifecycle */

  useEffect(() => {
      if (isSimCardLoading || isSimCardError) return;
      dispatch(logBusinessEvent({
          eventName: ELogBusinessEventName.B2B_CHANGE_SIM_MASTER,
          type: ELogBusinessEventType.common,
      }));
  }, [isSimCardLoading, dispatch, isSimCardError])

  useEffect(() => {
    if (queryParams) {
      fetchNextPage({ ...queryParams, subscriberId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  /* Handle Events */

  const onSubmit = (
    value: { icc: IInputSearchItem },
    { setSubmitting, setFieldError }: FormikHelpers<{ icc: IInputSearchItem }>,
  ) => {
      dispatch(logBusinessEvent({
          eventName: ELogBusinessEventName.USER_CHANGE_SIM_CREATE_ORDER,
          type: ELogBusinessEventType.common,
      }));
    updateSimCard({
      iccId: value.icc.value,
      oldIccId: simCard?.icc,
      oldSimCardId: simCard?.simCardId,
      subscriberId,
    })
      .unwrap()
      .then((data: IManageNumberSimCardUpdateResponse) => {
        if (data?.fieldErrors && data.fieldErrors.length > 0) {
          dispatch(modalSetStatusAction({ status: EModalStatus.start }));
          setFieldError('icc.value', data.fieldErrors[0].errorMessage);
        } else if (data?.orderId) {
          dispatch(modalSetStatusAction({ status: EModalStatus.success }));
        } else {
          dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
        }
      })
      .catch(() => {
        dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (!simCard) return <Fragment />;

  return (
    <Modal dataName="manage-number-sim-card-change" width="580px">
      <Modal.Status status={EModalStatus.start}>
        <Modal.Title>
          {!simCard.icc
            ? translationManageNumber.simCard.modal.title.add
            : translationManageNumber.simCard.modal.title.change}
        </Modal.Title>
        <Modal.Text>
          {isAllowSimCardSuggest
            ? translationManageNumber.simCard.modal.suggestSubTitle
            : translationManageNumber.simCard.modal.subTitle}
        </Modal.Text>
        <div style={{ marginTop: 16 }}>
          <Formik
            initialValues={initialValue}
            validationSchema={getSimCardValidation(
              tForm.validation,
              isAllowSimCardSuggest,
            )}
            onSubmit={onSubmit}
          >
            {({ dirty, getFieldProps, isSubmitting, isValid, submitForm }) => (
              <Fragment>
                <FormFieldInputSearch
                  dataName="manage-number-sim-card-change-input"
                  foundItems={data}
                  hideValidation={
                    getFieldProps('icc').value?.value.length === 0
                  }
                  icon={
                    isAllowSimCardSuggest
                      ? isLoading
                        ? icons.searchIndicator
                        : icons.searchSmallIcon
                      : undefined
                  }
                  max={20}
                  name="icc"
                  placeholder={
                    translationManageNumber.simCard.modal.placeholder
                  }
                  regex={new RegExp(/^\d+$/)}
                  virtual={{
                    hasMore,
                    isLoading: isLoading,
                    onScrollEvent: () =>
                      setQueryParams({
                        searchValue: getFieldProps('icc').value.value,
                        start: false,
                      }),
                  }}
                  onChange={(searchValue: string) => {
                    isAllowSimCardSuggest &&
                      setQueryParams({
                        searchValue,
                        start: true,
                      });
                  }}
                />
                <ManageNumberSimCardNoDataStyled>
                  {isAllowSimCardSuggest && !data.length && dirty && (
                    <div>{translationManageNumber.simCard.noFoundItems}</div>
                  )}
                </ManageNumberSimCardNoDataStyled>

                <Modal.Control>
                  <ButtonServiceUserDenied
                    disabled={!isValid || isSubmitting || !dirty}
                    kind="medium"
                    type="submit"
                    onClick={submitForm}
                  >
                    {!simCard.icc
                      ? translationManageNumber.simCard.btn.add
                      : translationManageNumber.simCard.btn.change}
                  </ButtonServiceUserDenied>
                  <LinkBlock onClick={onCloseModal}>{tModal.cancel}</LinkBlock>
                </Modal.Control>
              </Fragment>
            )}
          </Formik>
        </div>
      </Modal.Status>

      <Modal.Status status={EModalStatus.success}>
        {simCardUpdateResult && (
          <ModalOrder
            orderId={simCardUpdateResult.orderId}
            onCloseModal={onCloseModal}
          />
        )}
      </Modal.Status>

      <Modal.Status status={EModalStatus.failure}>
        <ModalCenterStyled>
          <ImgStatusError />
          <Modal.Title>{tModal.error}</Modal.Title>
          <Modal.Text>{tModal.errorMsg.defaultMessage}</Modal.Text>
          <Modal.Control>
            <Button kind="medium" onClick={onCloseModal}>
              {tModal.close}
            </Button>
          </Modal.Control>
        </ModalCenterStyled>
      </Modal.Status>
    </Modal>
  );
};

export default ManageNumberSimCardModal;
